/**
 * ONE SHOUT-OUT WALL CARD, DEFINED ONCE, FOR THE SERVER AND THE PAGE.
 *
 * Since 2026-09-29 only the server writes the wall. Bobbie, verbatim: "Yes,
 * for the award section, I think it should refuse anything that doesn't come
 * from the green put on the wall button". So the green button calls
 * netlify/functions/studentAwardAction.mjs publishMonthWall, which writes
 * every card with the Admin SDK, and shared/firestore.rules refuses every
 * client create and update on studentAwardWins. The page still needs the id
 * (to know what is already up, and for Take down), so the id and the fields
 * live here, in a file with no imports at all, which the server, the page and
 * a plain node test can all load.
 *
 * NAMES AND AWARDS ONLY (Bobbie, 2026-08-05: "Families see NAMES AND AWARDS
 * ONLY. The nomination notes stay private."). A card is the dancer, the
 * award, the month, and `fromName`, the display name of whoever made the pick
 * (the wall shows "from Ms. Tara"). There is NO note field and NO nominator
 * uid, on purpose, and fromName is a display name from teacherDisplayName,
 * never an email.
 *
 * EVERY CARD SAYS IT CAME FROM THE WALL BUTTON: postedVia 'wall-button', and
 * the writer stamps postedAt. Cards the old approve flow wrote before #1266
 * are still in the collection and carry announcedAt too, so announcedAt
 * cannot tell them apart; postedVia can. The wall button stamps an old card
 * for an approved pick when Kim taps it (same id, its announcedAt kept).
 *
 * THE FILTER LIVES HERE (keepWallButtonCards) and it is not optional. The wall
 * shows, counts and searches ONLY cards that carry the mark, for EVERY viewer,
 * a Director included, so Kim's wall and her awards page agree about what is up
 * (lib/awardWallState.ts splits on the same mark). It is applied by the shared
 * reads in lib/studentAwardWins.ts (the wall's school-year and one-month
 * readers, the stars and pills, the Monthly recipients card) and again by the
 * wall page. The one exception is the Director's Student Awards page, which
 * lists the cards the old approve flow left, apart, so she can stamp or take
 * them down (useSchoolYearAwardWins with includeOldFlow). It landed with the
 * Shout-out Wall redesign (PR #1274), not with the un-pause; the un-pause only
 * flips AWARDS_HIDDEN_UNTIL_POSTED (lib/wallPause.ts), and wall-pause.test.mjs
 * fails if that switch is ever false without this filter in place.
 *
 * The Jr Staff MVP rides the same step with award type 'jr-staff-mvp' and no
 * fromName (the teachers voted; nobody picked her alone). A Jr Staff with no
 * dancer record has no studentId, so her card is keyed by her person uid,
 * which stands in as its studentId.
 */

export type AwardWinType = 'student-of-month' | 'jr-spotlight' | 'jr-staff-mvp';

/** The mark every card the wall button writes carries, and no older card does. */
export const AWARD_WIN_POSTED_VIA = 'wall-button';

/** True for a card the wall button wrote: it carries exactly the mark, and nothing older does. */
export function isWallButtonCard(card: { postedVia?: unknown } | null | undefined): boolean {
  return !!card && card.postedVia === AWARD_WIN_POSTED_VIA;
}

/**
 * Only the cards the wall button wrote. Everything a family can see and every
 * count built on the wall goes through this (see the note at the top of this
 * file); a card without the mark is refused, whoever is looking.
 */
export function keepWallButtonCards<T extends { postedVia?: unknown }>(cards: readonly T[]): T[] {
  return cards.filter((card) => isWallButtonCard(card));
}

/** The labels a card can carry. The first two mirror STUDENT_AWARD_LABELS in lib/studentAwards.ts. */
export const AWARD_WIN_LABELS: Record<AwardWinType, string> = {
  'student-of-month': 'Dancer of the Month',
  'jr-spotlight': 'Spotlight of the Month',
  'jr-staff-mvp': 'Jr Staff MVP',
};

/** The three public facts, in one id, so one dancer, one award, one month is one card. */
export function awardWinDocId(periodKey: string, awardType: AwardWinType, studentId: string): string {
  return `${periodKey}_${awardType}_${studentId}`;
}

/** '2026-10' is the 2026-2027 school year, which starts in August. */
export function awardWinSchoolYearKey(periodKey: string): string {
  const [year, month] = periodKey.split('-').map(Number);
  if (!year || !month) return periodKey;
  const start = month >= 8 ? year : year - 1;
  return `${start}-${start + 1}`;
}

/**
 * Who made the pick, for the card: every distinct display name, in a stable
 * order, "A and B" or "A, B and C". Blank names and the "Someone" placeholder
 * teacherDisplayName uses for an account with no name are left out; with
 * nothing left the card simply carries no fromName.
 */
export function awardFromName(names: readonly (string | null | undefined)[], placeholder = 'Someone'): string {
  const clean = [...new Set(
    names.map((name) => (typeof name === 'string' ? name.trim() : '')).filter((name) => name && name !== placeholder && !name.includes('@'))
  )].sort((a, b) => a.localeCompare(b));
  if (clean.length <= 1) return clean[0] ?? '';
  return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
}

export interface AwardWinCardSource {
  periodKey: string;
  awardType: AwardWinType;
  studentId: string;
  studentName: string;
  fromName?: string;
}

/**
 * Every field a card carries except the moments it went up (announcedAt and
 * postedAt), which the writer stamps. Built here, in one place, so no caller can quietly add a field:
 * this is what families read.
 */
export function awardWinCardFields(source: AwardWinCardSource) {
  const fromName = (source.fromName ?? '').trim().slice(0, 180);
  return {
    periodKey: source.periodKey,
    schoolYearKey: awardWinSchoolYearKey(source.periodKey),
    awardType: source.awardType,
    studentId: source.studentId,
    studentName: source.studentName.trim().slice(0, 180),
    ...(fromName && source.awardType !== 'jr-staff-mvp' ? { fromName } : {}),
    postedVia: AWARD_WIN_POSTED_VIA,
  };
}
